import React, { useEffect, useState, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  StaffMember, CreateStaffDTO, UpdateStaffDTO,
  CIMT_ROLES, DAYS, SystemRole, CimtRole, Day,
} from '../types/staff';
import { listStaff, createStaff, updateStaff, sendInvite, deleteStaff } from '../api/staff';
import { getRoster, assignRole, copyRoster, sendRosterReminder, listCimtRoles, addCimtRole, deleteCimtRole, RosterEntry, CimtRoleEntry } from '../api/roster';
import { useTheme, Theme } from '../hooks/useTheme';
import { StakeholdersPanel } from '../components/StakeholdersPanel';
import { IntegrationsPanel } from './settings/IntegrationsPanel';
import { IntegrationsSetupGuide } from './settings/IntegrationsSetupGuide';
import { useStaffProfile } from '../hooks/useStaffProfile';

// ── Constants ────────────────────────────────────────────────────────────────
const ROLE_COLOURS: Record<SystemRole, string> = {
  ADMIN: '#dc2626', MANAGER: '#ea580c', USER: '#2dd4bf',
};
const DAY_SHORT: Record<Day, string> = {
  MON: 'M', TUE: 'T', WED: 'W', THU: 'T', FRI: 'F', SAT: 'S', SUN: 'S',
};
const DAY_FULL: Record<Day, string> = {
  MON: 'Mon', TUE: 'Tue', WED: 'Wed', THU: 'Thu', FRI: 'Fri', SAT: 'Sat', SUN: 'Sun',
};

// ── Actions Dropdown (portal-based so table rows can't clip it) ──────────────
function ActionsDropdown({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node) &&
          btnRef.current && !btnRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleToggle = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.left });
    }
    setOpen(!open);
  };

  return (
    <>
      <button
        ref={btnRef}
        onClick={handleToggle}
        style={{
          background: open ? 'var(--primary-dim)' : 'var(--surface)',
          border: `1px solid ${open ? 'var(--primary-border)' : 'var(--border)'}`,
          borderRadius: 6, padding: '4px 10px', cursor: 'pointer',
          color: open ? 'var(--primary)' : 'var(--muted)',
          fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4,
        }}
      >
        Actions
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && ReactDOM.createPortal(
        <div ref={menuRef} style={{
          position: 'fixed', top: pos.top, left: pos.left, minWidth: 160, zIndex: 9999,
          background: 'var(--card)', border: '1px solid var(--border-md)', borderRadius: 8,
          boxShadow: '0 12px 32px rgba(0,0,0,0.22)', overflow: 'hidden',
        }}>
          {React.Children.map(children, (child) =>
            child ? <div onClick={() => setOpen(false)}>{child}</div> : null
          )}
        </div>,
        document.body,
      )}
    </>
  );
}

function ActionItem({ label, color, onClick }: { label: string; color?: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      onMouseEnter={e => (e.currentTarget.style.background = 'var(--primary-dim)')}
      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
      style={{
        width: '100%', textAlign: 'left', background: 'transparent', border: 'none',
        padding: '8px 14px', fontSize: 12, color: color || 'var(--fg)',
        cursor: 'pointer', transition: 'background 0.1s',
      }}
    >
      {label}
    </button>
  );
}

// ── Invite / Onboard Modal ──────────────────────────────────────────────────
interface InviteModalProps {
  onDone: () => void;
  onClose: () => void;
  /** Full role list (base + per-client custom). Falls back to CIMT_ROLES if
   *  the parent hasn't loaded them yet. */
  availableRoles: string[];
}

function InviteModal({ onDone, onClose, availableRoles }: InviteModalProps) {
  const [mode, setMode] = useState<'manual' | 'csv'>('manual');
  const [rows, setRows] = useState([{ email: '', firstName: '', lastName: '' }]);
  const [role, setRole] = useState<SystemRole>('USER');
  const [cimtRole, setCimtRole] = useState<string>('');
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState<{ email: string; status: string; link?: string }[] | null>(null);

  const addRow = () => setRows([...rows, { email: '', firstName: '', lastName: '' }]);
  const removeRow = (i: number) => setRows(rows.filter((_, idx) => idx !== i));
  const updateRow = (i: number, field: string, val: string) =>
    setRows(rows.map((r, idx) => idx === i ? { ...r, [field]: val } : r));

  const downloadTemplate = () => {
    const csv = 'email,first_name,last_name,role,cimt_role\nexample@company.com,John,Doe,USER,Operations\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = 'cima-invite-template.csv'; a.click();
  };

  const handleSubmit = async () => {
    setSaving(true); setError('');
    try {
      const created: { email: string; status: string; link?: string }[] = [];
      if (mode === 'csv' && csvFile) {
        const text = await csvFile.text();
        for (const line of text.trim().split('\n').slice(1)) {
          const [email, firstName, lastName, csvRole, csvCimt] = line.split(',').map(s => s.trim());
          if (!email || !firstName || !lastName) continue;
          try {
            const s = await createStaff({ email, firstName, lastName,
              systemRole: (['ADMIN','MANAGER','USER'].includes(csvRole?.toUpperCase()) ? csvRole.toUpperCase() : role) as SystemRole,
              // Accept any role in the effective list (base + custom for this client).
              cimtRole: (csvCimt && availableRoles.includes(csvCimt)) ? csvCimt : undefined,
            });
            const res = await sendInvite(s.id);
            created.push({ email, status: 'Invited', link: res.inviteLink });
          } catch (err: any) { created.push({ email, status: err.message }); }
        }
      } else {
        const valid = rows.filter(r => r.email.trim() && r.firstName.trim() && r.lastName.trim());
        if (!valid.length) { setError('At least one complete row required'); setSaving(false); return; }
        for (const r of valid) {
          try {
            const s = await createStaff({ email: r.email, firstName: r.firstName, lastName: r.lastName,
              systemRole: role, cimtRole: cimtRole || undefined });
            const res = await sendInvite(s.id);
            created.push({ email: r.email, status: 'Invited', link: res.inviteLink });
          } catch (err: any) { created.push({ email: r.email, status: err.message }); }
        }
      }
      setResults(created);
    } catch (err: any) { setError(err.message); } finally { setSaving(false); }
  };

  if (results) {
    const ok = results.filter(r => r.status === 'Invited').length;
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-card staff-modal" onClick={e => e.stopPropagation()}>
          <h3 style={{ marginBottom: 16 }}>Invitation Results</h3>
          <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
            <div style={{ flex: 1, background: 'rgba(22,163,74,0.1)', borderRadius: 8, padding: 12, textAlign: 'center' }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--success)' }}>{ok}</div>
              <div style={{ fontSize: 11, color: 'var(--muted)' }}>Invited</div>
            </div>
            {results.length - ok > 0 && (
              <div style={{ flex: 1, background: 'rgba(220,38,38,0.1)', borderRadius: 8, padding: 12, textAlign: 'center' }}>
                <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--accent-red)' }}>{results.length - ok}</div>
                <div style={{ fontSize: 11, color: 'var(--muted)' }}>Failed</div>
              </div>
            )}
          </div>
          <div style={{ maxHeight: 200, overflowY: 'auto', marginBottom: 16 }}>
            {results.map((r, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '7px 10px', borderBottom: '1px solid var(--border)', fontSize: 12 }}>
                <span>{r.email}</span>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 4,
                    background: r.status === 'Invited' ? 'var(--success-soft)' : 'rgba(220,38,38,0.15)',
                    color: r.status === 'Invited' ? 'var(--success)' : 'var(--accent-red)' }}>{r.status}</span>
                  {r.link && <button onClick={() => navigator.clipboard.writeText(r.link!)} style={{
                    background: 'transparent', border: '1px solid var(--border)', borderRadius: 4,
                    color: 'var(--primary)', fontSize: 10, padding: '2px 6px', cursor: 'pointer' }}>Copy</button>}
                </div>
              </div>
            ))}
          </div>
          <button className="submit-btn" onClick={() => { onDone(); onClose(); }} style={{ width: '100%' }}>Done</button>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card staff-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 580 }}>
        <h3 style={{ marginBottom: 4 }}>Onboard New Staff</h3>
        <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 16 }}>Add team members and send invitations</p>

        <div style={{ display: 'flex', gap: 0, marginBottom: 14, border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
          {(['manual', 'csv'] as const).map(m => (
            <button key={m} onClick={() => setMode(m)} style={{
              flex: 1, padding: '7px 0', fontSize: 12, cursor: 'pointer', border: 'none',
              fontWeight: mode === m ? 700 : 600,
              background: mode === m ? 'color-mix(in srgb, var(--primary) 14%, var(--card))' : 'var(--surface)',
              color: mode === m ? 'var(--primary)' : 'var(--muted)',
              transition: 'background 0.12s, color 0.12s',
            }}>{m === 'manual' ? 'Manual Entry' : 'CSV Upload'}</button>
          ))}
        </div>

        {mode === 'manual' ? (
          <>
            {rows.map((r, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 0.7fr 0.7fr 24px', gap: 6, marginBottom: 6 }}>
                <input className="form-input" placeholder="Email" value={r.email} onChange={e => updateRow(i, 'email', e.target.value)} style={{ fontSize: 12 }} />
                <input className="form-input" placeholder="First Name" value={r.firstName} onChange={e => updateRow(i, 'firstName', e.target.value)} style={{ fontSize: 12 }} />
                <input className="form-input" placeholder="Last Name" value={r.lastName} onChange={e => updateRow(i, 'lastName', e.target.value)} style={{ fontSize: 12 }} />
                {rows.length > 1 && <button onClick={() => removeRow(i)} style={{ background: 'transparent', border: 'none', color: 'var(--accent-red)', cursor: 'pointer', fontSize: 14 }}>×</button>}
              </div>
            ))}
            <button onClick={addRow} style={{ background: 'transparent', border: '1px dashed var(--border)', borderRadius: 6,
              color: 'var(--primary)', fontSize: 11, padding: '5px 0', width: '100%', cursor: 'pointer', marginBottom: 10 }}>+ Add Another</button>
          </>
        ) : (
          <div style={{ marginBottom: 10 }}>
            <div onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = 'var(--primary)'; }}
              onDragLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; }}
              onDrop={e => { e.preventDefault(); e.currentTarget.style.borderColor = 'var(--border)';
                const f = e.dataTransfer.files[0]; if (f?.name.endsWith('.csv')) setCsvFile(f); }}
              onClick={() => document.getElementById('csv-up')?.click()}
              style={{ border: `2px dashed ${csvFile ? 'var(--primary)' : 'var(--border)'}`, borderRadius: 8,
                padding: '20px 16px', textAlign: 'center', cursor: 'pointer',
                background: csvFile ? 'var(--primary-dim)' : 'transparent' }}>
              <input type="file" id="csv-up" accept=".csv" hidden onChange={e => setCsvFile(e.target.files?.[0] ?? null)} />
              {csvFile ? <span style={{ color: 'var(--primary)', fontWeight: 600, fontSize: 13 }}>{csvFile.name}</span>
                : <><div style={{ fontSize: 20, marginBottom: 4 }}>📄</div><div style={{ fontSize: 12, color: 'var(--muted)' }}>Drop CSV or click to browse</div></>}
            </div>
            <button onClick={downloadTemplate} style={{ background: 'transparent', border: 'none', color: 'var(--primary)', fontSize: 11, marginTop: 4, cursor: 'pointer', textDecoration: 'underline' }}>Download template</button>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
          <div className="form-group"><label className="form-label">System Role</label>
            <select className="form-input" value={role} onChange={e => setRole(e.target.value as SystemRole)}>
              <option value="USER">User</option><option value="MANAGER">Manager</option><option value="ADMIN">Admin</option>
            </select></div>
          <div className="form-group"><label className="form-label">CIMT Role</label>
            <select className="form-input" value={cimtRole} onChange={e => setCimtRole(e.target.value)}>
              <option value="">— None —</option>{availableRoles.map(r => <option key={r} value={r}>{r}</option>)}
            </select></div>
        </div>

        {error && <p style={{ color: 'var(--accent-red)', fontSize: 12, marginBottom: 6 }}>{error}</p>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="cancel-btn" onClick={onClose}>Cancel</button>
          <button className="submit-btn" onClick={handleSubmit} disabled={saving}>{saving ? 'Sending...' : 'Send Invitations'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Edit Modal ──────────────────────────────────────────────────────────────
function EditModal({ staff, onSave, onClose, availableRoles }: { staff: StaffMember; onSave: (d: UpdateStaffDTO) => Promise<void>; onClose: () => void; availableRoles: string[] }) {
  const [firstName, setFirst] = useState(staff.firstName);
  const [lastName, setLast] = useState(staff.lastName);
  const [phone, setPhone] = useState(staff.phone ?? '');
  const [jobTitle, setJob] = useState(staff.jobTitle ?? '');
  const [sysRole, setSysRole] = useState<SystemRole>(staff.systemRole);
  const [cimtRole, setCimtRole] = useState<string>(staff.cimtRole ?? '');
  const [days, setDays] = useState<Day[]>(staff.availableDays ?? ['MON','TUE','WED','THU','FRI']);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const toggleDay = (d: Day) => setDays(p => p.includes(d) ? p.filter(x => x !== d) : [...p, d]);

  const save = async () => {
    setSaving(true); setError('');
    try { await onSave({ firstName: firstName.trim(), lastName: lastName.trim(), phone: phone.trim() || undefined,
      jobTitle: jobTitle.trim() || undefined, systemRole: sysRole, cimtRole: cimtRole || undefined, availableDays: days });
    } catch (err: any) { setError(err.message); } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card staff-modal" onClick={e => e.stopPropagation()}>
        <h3 style={{ marginBottom: 4 }}>Edit Staff Member</h3>
        <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 14 }}>{staff.email}</p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div className="form-group"><label className="form-label">First Name</label><input className="form-input" value={firstName} onChange={e => setFirst(e.target.value)} /></div>
          <div className="form-group"><label className="form-label">Last Name</label><input className="form-input" value={lastName} onChange={e => setLast(e.target.value)} /></div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div className="form-group"><label className="form-label">Phone</label><input className="form-input" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+61 4xx xxx xxx" /></div>
          <div className="form-group"><label className="form-label">Job Title</label><input className="form-input" value={jobTitle} onChange={e => setJob(e.target.value)} /></div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div className="form-group"><label className="form-label">System Role</label>
            <select className="form-input" value={sysRole} onChange={e => setSysRole(e.target.value as SystemRole)}>
              <option value="ADMIN">Admin</option><option value="MANAGER">Manager</option><option value="USER">User</option></select></div>
          <div className="form-group"><label className="form-label">CIMT Role</label>
            <select className="form-input" value={cimtRole} onChange={e => setCimtRole(e.target.value)}>
              <option value="">— None —</option>{availableRoles.map(r => <option key={r} value={r}>{r}</option>)}</select></div>
        </div>
        <div className="form-group">
          <label className="form-label">Available Days (On Call)</label>
          <div style={{ display: 'flex', gap: 5 }}>
            {DAYS.map(d => (
              <button key={d} type="button" onClick={() => toggleDay(d)} title={DAY_FULL[d]} style={{
                width: 34, height: 34, borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 600,
                background: days.includes(d) ? 'var(--primary)' : 'var(--card)', color: days.includes(d) ? '#0d0e14' : 'var(--muted)',
              }}>{DAY_SHORT[d]}</button>
            ))}
          </div>
        </div>
        {error && <p style={{ color: 'var(--accent-red)', fontSize: 12, marginTop: 6 }}>{error}</p>}
        <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
          <button className="cancel-btn" onClick={onClose}>Cancel</button>
          <button className="submit-btn" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ──────────────────────────────────────────────────────────
type SettingsTab = 'staff' | 'invitations' | 'roster' | 'cimt-roles' | 'stakeholders' | 'integrations' | 'appearance';

interface StaffManagementProps {
  /** Optional initial tab so other parts of the app (e.g. the Settings
   *  dropdown's "Appearance · Theme" link) can deep-link to a specific tab. */
  initialTab?: SettingsTab;
}

export function StaffManagement({ initialTab = 'staff' }: StaffManagementProps = {}) {
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<SettingsTab>(initialTab);
  const [showIntegrationsGuide, setShowIntegrationsGuide] = useState(false);

  // Listen for cross-component deep links — e.g. App.tsx's "Set up Microsoft
  // Teams" banner CTA fires `cima:open-settings-tab` with detail 'integrations'.
  React.useEffect(() => {
    const handler = (e: Event) => {
      const tab = (e as CustomEvent).detail as SettingsTab;
      if (tab) setActiveTab(tab);
    };
    window.addEventListener('cima:open-settings-tab', handler);
    return () => window.removeEventListener('cima:open-settings-tab', handler);
  }, []);

  // If the parent hands us a new initialTab (e.g. user clicks a different
  // dropdown item while already on Settings), honour it.
  useEffect(() => { setActiveTab(initialTab); }, [initialTab]);
  const [showInvite, setShowInvite] = useState(false);
  const [editTarget, setEditTarget] = useState<StaffMember | null>(null);
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState<'' | SystemRole>('');
  const [filterCimt, setFilterCimt] = useState('');
  const [copiedId, setCopiedId] = useState<number | null>(null);
  // Weekly roster state
  const [rosterWeek, setRosterWeek] = useState(() => {
    // Deep-link from the weekly roster reminder email — App.tsx stashes the
    // target week here before navigating to this tab. Consume it once.
    const deepLinked = sessionStorage.getItem('cima:roster-week');
    if (deepLinked) {
      sessionStorage.removeItem('cima:roster-week');
      if (/^\d{4}-\d{2}-\d{2}$/.test(deepLinked)) return deepLinked;
    }
    const d = new Date(); const day = d.getDay();
    d.setDate(d.getDate() - day + (day === 0 ? -6 : 1));
    // Use local date to avoid UTC timezone shift
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  });
  const [roster, setRoster] = useState<RosterEntry[]>([]);
  const [rosterLoading, setRosterLoading] = useState(false);
  const [showAddStakeholder, setShowAddStakeholder] = useState(false);
  const me = useStaffProfile();
  const canManageStakeholders = me?.cimaRole === 'ADMIN' || me?.cimaRole === 'MANAGER';

  // Effective CIMT role list (base 14 minus admin-hidden, plus per-client
  // custom). Loaded once on mount and refreshed whenever a role is
  // added/removed/restored so every dropdown (onboarding, edit modal,
  // filter, roster page) stays in sync. Falls back to the base list until
  // the API responds. `disabledRoles` powers the Restore chips.
  const [cimtRolesList, setCimtRolesList] = useState<CimtRoleEntry[]>(
    () => CIMT_ROLES.map(name => ({ name, isCustom: false })),
  );
  const [disabledRoles, setDisabledRoles] = useState<string[]>([]);
  const availableRoles = cimtRolesList.map(r => r.name);
  const refreshCimtRoles = useCallback(async () => {
    try {
      const data = await listCimtRoles();
      setCimtRolesList(data.roles);
      setDisabledRoles(data.disabled ?? []);
    } catch { /* keep the optimistic base list */ }
  }, []);
  useEffect(() => { refreshCimtRoles(); }, [refreshCimtRoles]);

  // Inline "add custom role" form state for the Weekly Roster tab.
  const [newRoleName, setNewRoleName] = useState('');
  const [addingRole, setAddingRole] = useState(false);
  const [roleError, setRoleError] = useState('');

  const loadRoster = useCallback(async (week: string) => {
    setRosterLoading(true);
    try { const data = await getRoster(week); setRoster(data.roster); }
    catch { setRoster([]); }
    finally { setRosterLoading(false); }
  }, []);

  useEffect(() => { if (activeTab === 'roster') loadRoster(rosterWeek); }, [activeTab, rosterWeek, loadRoster]);

  const handleAssign = async (
    cimtRole: string,
    primaryId: number | null,
    alternateId: number | null,
    secondaryAlternateId: number | null = null,
  ) => {
    await assignRole(rosterWeek, cimtRole, primaryId, alternateId, secondaryAlternateId);
    loadRoster(rosterWeek);
  };

  // Add a per-client custom CIMT role (e.g. "Onboarding Lead") and refresh
  // the list + the current week's roster (so the new row appears).
  const handleAddCustomRole = async () => {
    const name = newRoleName.trim();
    if (!name) { setRoleError('Enter a role name'); return; }
    setAddingRole(true); setRoleError('');
    try {
      await addCimtRole(name);
      setNewRoleName('');
      await refreshCimtRoles();
      await loadRoster(rosterWeek);
    } catch (err: any) {
      setRoleError(err.message || 'Failed to add role');
    } finally {
      setAddingRole(false);
    }
  };

  // Remove a role from this client's CIMT structure. For base roles this
  // hides them (restorable from the chips below); for custom roles this
  // permanently deletes the row. Either way the cascade wipes assignments
  // across all weeks so the contacts/CSV/agent stop seeing the role.
  // 'CIMT Leader' is structural — sign-off depends on it — so it cannot
  // be removed (the UI also doesn't render the × on that row).
  const handleRemoveRole = async (roleName: string, isCustom: boolean) => {
    const msg = isCustom
      ? `Remove "${roleName}" as a CIMT role? Any past or future roster assignments to this role will also be deleted.`
      : `Hide "${roleName}" from your CIMT structure? It won't appear in the roster, onboarding, or meeting contacts. Past assignments will be deleted. You can restore it from the "Hidden roles" list below.`;
    if (!window.confirm(msg)) return;
    try {
      await deleteCimtRole(roleName);
      await refreshCimtRoles();
      await loadRoster(rosterWeek);
    } catch (err: any) {
      alert(err.message || 'Failed to remove role');
    }
  };

  // Restore a previously-hidden base role. Same endpoint as Add custom
  // role — the backend recognises base names and removes the disable row.
  const handleRestoreRole = async (roleName: string) => {
    try {
      await addCimtRole(roleName);
      await refreshCimtRoles();
      await loadRoster(rosterWeek);
    } catch (err: any) {
      alert(err.message || 'Failed to restore role');
    }
  };

  const shiftWeek = (dir: number) => {
    const d = new Date(rosterWeek + 'T00:00:00'); // Force local timezone
    d.setDate(d.getDate() + dir * 7);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    setRosterWeek(`${yyyy}-${mm}-${dd}`);
  };

  const load = useCallback(async () => {
    try { setLoading(true); setStaff(await listStaff()); }
    catch (err: any) { setError(err.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleUpdate = async (dto: UpdateStaffDTO) => { if (!editTarget) return; await updateStaff(editTarget.id, dto); setEditTarget(null); load(); };
  const handleDelete = async (s: StaffMember) => {
    if (!window.confirm(`Delete ${s.firstName} ${s.lastName}? This cannot be undone.`)) return;
    try { await deleteStaff(s.id); load(); } catch (err: any) { alert('Delete failed: ' + err.message); }
  };
  const handleResendInvite = async (s: StaffMember) => {
    try { const res = await sendInvite(s.id); navigator.clipboard.writeText(res.inviteLink);
      setCopiedId(s.id); setTimeout(() => setCopiedId(null), 2000);
    } catch (err: any) { alert('Invite failed: ' + err.message); }
  };
  const handleToggleLeave = async (s: StaffMember) => { await updateStaff(s.id, { isOnLeave: !s.isOnLeave }); load(); };
  const handleToggleActive = async (s: StaffMember) => { await updateStaff(s.id, { isActive: !s.isActive }); load(); };

  // Derived
  const accepted = staff.filter(s => s.inviteAcceptedAt);
  const pending = staff.filter(s => !s.inviteAcceptedAt && s.inviteSentAt);
  const notInvited = staff.filter(s => !s.inviteSentAt);
  const q = search.toLowerCase();
  const displayList = activeTab === 'staff' ? accepted : activeTab === 'invitations' ? [...pending, ...notInvited] : [];
  const filtered = displayList.filter(s => {
    if (filterRole && s.systemRole !== filterRole) return false;
    if (filterCimt && s.cimtRole !== filterCimt) return false;
    if (q) return s.firstName.toLowerCase().includes(q) || s.lastName.toLowerCase().includes(q) ||
      s.email.toLowerCase().includes(q) || (s.cimtRole ?? '').toLowerCase().includes(q) || (s.jobTitle ?? '').toLowerCase().includes(q);
    return true;
  });

  const assignedCount = roster.filter(r => r.primary || r.alternate).length;

  return (
    <div className="dashboard">
      {/* ── Header bar ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--fg)', margin: 0 }}>Manage Staff Members</h1>
          <p style={{ fontSize: 13, color: 'var(--muted)', marginTop: 2 }}>
            {staff.length} total · {accepted.length} active · {pending.length} pending
          </p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'stretch' }}>
          <button className="submit-btn" onClick={() => setShowInvite(true)} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Onboard Staff
          </button>
          {canManageStakeholders && (
            <button
              onClick={() => { setActiveTab('stakeholders'); setShowAddStakeholder(true); }}
              style={{
                background: 'transparent', border: '1px solid var(--primary-border)',
                color: 'var(--primary)', borderRadius: 8, padding: '8px 14px',
                fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Add Stakeholder
            </button>
          )}
        </div>
      </div>

      {/* ── Filters bar ── */}
      <div style={{
        background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 10,
        padding: '14px 18px', marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap',
      }}>
        <div style={{ flex: '1 1 200px' }}>
          <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--subtle)', display: 'block', marginBottom: 3, textTransform: 'uppercase' }}>Search</label>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Name, email, title..."
            style={{ width: '100%', padding: '7px 10px', fontSize: 12, background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: 6, color: 'var(--fg)', outline: 'none' }} />
        </div>
        <div style={{ flex: '0 0 130px' }}>
          <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--subtle)', display: 'block', marginBottom: 3, textTransform: 'uppercase' }}>By Role</label>
          <select value={filterRole} onChange={e => setFilterRole(e.target.value as any)}
            style={{ width: '100%', padding: '7px 10px', fontSize: 12, background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: 6, color: 'var(--fg)' }}>
            <option value="">All Roles</option><option value="ADMIN">Admin</option><option value="MANAGER">Manager</option><option value="USER">User</option>
          </select>
        </div>
        <div style={{ flex: '0 0 170px' }}>
          <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--subtle)', display: 'block', marginBottom: 3, textTransform: 'uppercase' }}>By CIMT Role</label>
          <select value={filterCimt} onChange={e => setFilterCimt(e.target.value)}
            style={{ width: '100%', padding: '7px 10px', fontSize: 12, background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: 6, color: 'var(--fg)' }}>
            <option value="">All CIMT Roles</option>{availableRoles.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div style={{ flex: '0 0 auto', alignSelf: 'flex-end' }}>
          <button onClick={() => { setSearch(''); setFilterRole(''); setFilterCimt(''); }}
            style={{ padding: '7px 14px', fontSize: 11, fontWeight: 600, background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: 6, color: 'var(--muted)', cursor: 'pointer' }}>Reset</button>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div data-tour="settings.tabs" style={{ display: 'flex', gap: 0, marginBottom: 0, borderBottom: '1px solid var(--border)', background: 'var(--card)',
        borderTopLeftRadius: 10, borderTopRightRadius: 10, border: '1px solid var(--border)', borderBottomColor: 'var(--border)' }}>
        {([
          { key: 'staff' as const, label: 'Active Staff', count: accepted.length, showCount: true },
          { key: 'invitations' as const, label: 'Invitations', count: pending.length + notInvited.length, showCount: true },
          { key: 'roster' as const, label: 'Weekly Roster', count: assignedCount, showCount: true },
          { key: 'cimt-roles' as const, label: 'CIMT Roles', count: cimtRolesList.length, showCount: true },
          { key: 'stakeholders' as const, label: 'Stakeholders', count: 0, showCount: false },
          { key: 'integrations' as const, label: 'Integrations', count: 0, showCount: false },
          { key: 'appearance' as const, label: 'Appearance', count: 0, showCount: false },
        ]).map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            data-tour={`settings.tab.${tab.key === 'cimt-roles' ? 'cimtroles' : tab.key}`}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 7,
              background: 'transparent', border: 'none',
              borderBottom: activeTab === tab.key ? '2px solid var(--primary)' : '2px solid transparent',
              marginBottom: -1,
              padding: '13px 22px', cursor: 'pointer', fontSize: 13,
              fontWeight: activeTab === tab.key ? 700 : 600,
              color: activeTab === tab.key ? 'var(--primary)' : 'var(--muted)',
              transition: 'color 0.12s',
            }}
            onMouseEnter={e => { if (activeTab !== tab.key) e.currentTarget.style.color = 'var(--fg)'; }}
            onMouseLeave={e => { if (activeTab !== tab.key) e.currentTarget.style.color = 'var(--muted)'; }}
          >
            {tab.label}
            {tab.showCount && (
              <span style={{
                fontSize: 10.5, fontWeight: 700, lineHeight: 1.6, minWidth: 18, textAlign: 'center',
                padding: '0 6px', borderRadius: 999,
                background: activeTab === tab.key ? 'color-mix(in srgb, var(--primary) 14%, transparent)' : 'var(--surface)',
                color: activeTab === tab.key ? 'var(--primary)' : 'var(--subtle)',
              }}>{tab.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* ── Content ── */}
      <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderTop: 'none',
        borderBottomLeftRadius: 10, borderBottomRightRadius: 10, overflow: 'visible' }}>

        {activeTab === 'stakeholders' ? (
          <StakeholdersPanel
            canManage={canManageStakeholders}
            showAddModal={showAddStakeholder}
            onCloseAddModal={() => setShowAddStakeholder(false)}
          />
        ) : activeTab === 'integrations' ? (
          showIntegrationsGuide ? (
            <IntegrationsSetupGuide onBack={() => setShowIntegrationsGuide(false)} />
          ) : (
            <IntegrationsPanel onOpenSetupGuide={() => setShowIntegrationsGuide(true)} />
          )
        ) : activeTab === 'appearance' ? (
          <AppearanceSettings />
        ) : activeTab === 'roster' ? (
          (() => {
            // ── Roster presentation helpers ──────────────────────────────
            const mono = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';
            const CORE = new Set(['CIMT Leader', 'Communication', 'Operations', 'Planning', 'Management Support', 'People & Welfare']);
            const coreRows = roster.filter(r => CORE.has(r.cimtRole));
            const supportRows = roster.filter(r => !CORE.has(r.cimtRole));
            const total = roster.length;
            const pct = total ? Math.round((assignedCount / total) * 100) : 0;
            const weekStart = new Date(rosterWeek + 'T00:00:00');
            const weekEnd = (() => { const d = new Date(rosterWeek + 'T00:00:00'); d.setDate(d.getDate() + 6); return d; })();
            const navBtn: React.CSSProperties = {
              width: 32, height: 32, borderRadius: 9, flexShrink: 0, background: 'var(--surface)',
              border: '1px solid var(--border-md)', color: 'var(--muted)', cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            };
            // Shared column template: role | primary | › | alt1 | › | alt2 | status
            const GRID = 'minmax(150px, 1.15fr) minmax(0, 1.7fr) 16px minmax(0, 1.7fr) 16px minmax(0, 1.7fr) 88px';
            const colHead: React.CSSProperties = { fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--subtle)' };
            const arrowCell = (
              <span style={{ display: 'grid', placeItems: 'center', color: 'var(--subtle)' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>
              </span>
            );
            const toOpt = (s: StaffMember): RosterOpt => ({ id: s.id, name: `${s.firstName} ${s.lastName}`, jobTitle: s.jobTitle ?? undefined });

            const renderRow = (entry: RosterEntry) => {
              const activeStaff = staff.filter(s => s.isActive && !s.isOnLeave);
              const matchedStaff = activeStaff.filter(s => s.cimtRole === entry.cimtRole);
              const matchedIds = new Set(matchedStaff.map(s => s.id));
              const otherStaff = activeStaff.filter(s => !matchedIds.has(s.id));
              const assignedInRow = new Set([entry.primaryId, entry.alternateId, entry.secondaryAlternateId].filter(Boolean) as number[]);
              const excludeFor = (selfId: number | null) => { const ex = new Set(assignedInRow); if (selfId != null) ex.delete(selfId); return ex; };
              // Grouped options: onboarded-for-role first, then everyone else.
              const groupsFor = (excludeIds: Set<number>) => {
                const m = matchedStaff.filter(s => !excludeIds.has(s.id)).map(toOpt);
                const o = otherStaff.filter(s => !excludeIds.has(s.id)).map(toOpt);
                const g: { label: string; items: RosterOpt[] }[] = [];
                if (m.length) g.push({ label: 'Onboarded for this role', items: m });
                if (o.length) g.push({ label: matchedStaff.length ? 'Other staff' : 'All staff', items: o });
                return g;
              };
              const selFor = (id: number | null | undefined) => { const s = staff.find(x => x.id === id); return s ? toOpt(s) : null; };
              const filled = [entry.primary, entry.alternate, entry.secondaryAlternate].filter(Boolean).length;
              return (
                <div key={entry.cimtRole} style={{ display: 'grid', gridTemplateColumns: GRID, alignItems: 'center', gap: 10, padding: '12px 18px', borderTop: '1px solid var(--border)' }}>
                  {/* Role */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--fg)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{entry.cimtRole}</span>
                    {entry.isCustom && (
                      <span style={{ flexShrink: 0, fontSize: 8, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: 'var(--primary-dim)', color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: 0.3 }} title="Custom role added by an admin">Custom</span>
                    )}
                    {entry.cimtRole === 'CIMT Leader' && (
                      <span style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 8, fontWeight: 700, padding: '2px 7px', borderRadius: 4, background: 'var(--success-soft)', color: 'var(--success)', textTransform: 'uppercase', letterSpacing: 0.3 }} title="Protected — IAP sign-off depends on this role and it cannot be removed">
                        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
                        Protected
                      </span>
                    )}
                  </div>
                  <RosterSlot placeholder="Select primary" selected={selFor(entry.primaryId)} groups={groupsFor(excludeFor(entry.primaryId))}
                    onSelect={(id) => handleAssign(entry.cimtRole, id, entry.alternateId, entry.secondaryAlternateId)} />
                  {arrowCell}
                  <RosterSlot placeholder="Select alternate 1" selected={selFor(entry.alternateId)} groups={groupsFor(excludeFor(entry.alternateId))}
                    onSelect={(id) => handleAssign(entry.cimtRole, entry.primaryId, id, entry.secondaryAlternateId)} />
                  {arrowCell}
                  <RosterSlot placeholder="Select alternate 2" selected={selFor(entry.secondaryAlternateId)} groups={groupsFor(excludeFor(entry.secondaryAlternateId))}
                    onSelect={(id) => handleAssign(entry.cimtRole, entry.primaryId, entry.alternateId, id)} />
                  {/* Status */}
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    {filled > 0 ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 9, fontWeight: 800, letterSpacing: '0.04em', padding: '4px 10px', borderRadius: 999, background: 'var(--success-soft)', color: 'var(--success)', whiteSpace: 'nowrap' }}
                        title={[entry.primary?.name, entry.alternate?.name, entry.secondaryAlternate?.name].filter(Boolean).join(' → ')}>
                        SET · {filled}/3
                      </span>
                    ) : (
                      <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.06em', padding: '4px 10px', borderRadius: 999, background: 'var(--faint)', color: 'var(--muted)' }}>EMPTY</span>
                    )}
                  </div>
                </div>
              );
            };

            const groupLabel = (label: string) => (
              <div key={`grp-${label}`} style={{ padding: '18px 18px 2px', fontFamily: mono, fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--subtle)' }}>
                {label}
              </div>
            );

            return (
          <div style={{ padding: '0 0 16px' }}>
            {/* ── Week navigator + progress ── */}
            <div style={{ padding: '16px 18px', borderBottom: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', rowGap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                  <button onClick={() => shiftWeek(-1)} style={navBtn} title="Previous week" aria-label="Previous week">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>
                  </button>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--fg)', letterSpacing: '-0.01em' }}>
                      Week of {weekStart.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </div>
                    <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>
                      {weekStart.toLocaleDateString('en-AU', { weekday: 'long' })} – {weekEnd.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'short' })}
                    </div>
                  </div>
                  <button onClick={() => shiftWeek(1)} style={navBtn} title="Next week" aria-label="Next week">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>
                  </button>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  {!rosterLoading && roster.every(r => !r.primaryId && !r.alternateId) && (
                    <button
                      onClick={async () => {
                        const d = new Date(rosterWeek + 'T00:00:00');
                        d.setDate(d.getDate() - 7);
                        const yyyy = d.getFullYear();
                        const mm = String(d.getMonth() + 1).padStart(2, '0');
                        const dd = String(d.getDate()).padStart(2, '0');
                        const prevWeek = `${yyyy}-${mm}-${dd}`;
                        try {
                          const r = await copyRoster(prevWeek, rosterWeek);
                          if (r.copied > 0) loadRoster(rosterWeek);
                          else alert('No assignments found in the previous week. Set up this week manually.');
                        } catch (e: any) { alert(e.message || 'Copy failed'); }
                      }}
                      style={{ background: 'var(--primary)', color: 'var(--on-primary)', border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}
                      title="Copy last week's CIMT assignments into this week — handy when nothing changes from one week to the next."
                    >
                      Copy previous week
                    </button>
                  )}
                  <SendReminderButton />
                </div>
              </div>
              {/* Assignment progress */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginTop: 14 }}>
                <div style={{ flex: 1, height: 6, borderRadius: 999, background: 'var(--faint)', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${pct}%`, background: pct === 100 ? 'var(--success)' : 'var(--primary)', borderRadius: 999, transition: 'width .25s' }} />
                </div>
                <span style={{ flexShrink: 0, fontFamily: mono, fontSize: 11.5, fontWeight: 600, color: 'var(--muted)' }}>{assignedCount} / {total} roles set</span>
              </div>
            </div>

            {rosterLoading ? (
              <p style={{ color: 'var(--muted)', padding: 32, textAlign: 'center' }}>Loading roster...</p>
            ) : (
              <div>
                {/* Column headers */}
                <div style={{ display: 'grid', gridTemplateColumns: GRID, alignItems: 'center', gap: 10, padding: '11px 18px', borderBottom: '1px solid var(--border)' }}>
                  <span style={colHead}>CIMT Role</span>
                  <span style={colHead}>Primary</span>
                  <span />
                  <span style={colHead}>Alternate 1</span>
                  <span />
                  <span style={colHead}>Alternate 2</span>
                  <span style={{ ...colHead, textAlign: 'right' }}>Status</span>
                </div>
                {/* Cascade explainer */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '10px 18px', background: 'var(--primary-dim)', borderBottom: '1px solid var(--border)', fontSize: 12, color: 'var(--muted)', lineHeight: 1.45 }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" /></svg>
                  <span>Sign-off authority cascades <strong style={{ color: 'var(--primary)' }}>Primary → Alternate 1 → Alternate 2</strong> — if the first is unavailable, it falls to the next.</span>
                </div>
                {/* Grouped rows */}
                {coreRows.length > 0 && groupLabel('Core CIMT')}
                {coreRows.map(renderRow)}
                {supportRows.length > 0 && groupLabel('Support Roles')}
                {supportRows.map(renderRow)}
              </div>
            )}
            {/* Role configuration lives in the dedicated CIMT Roles tab. */}
            <div style={{ padding: '10px 18px', borderTop: '1px solid var(--border)', fontSize: 11, color: 'var(--muted)' }}>
              Need to add or remove a role? Manage your CIMT structure in the <button onClick={() => setActiveTab('cimt-roles')} style={{ background: 'transparent', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontWeight: 700, padding: 0, fontSize: 11, fontFamily: 'inherit' }}>CIMT Roles</button> tab.
            </div>
          </div>
            );
          })()
        ) : activeTab === 'cimt-roles' ? (
          // ── CIMT Roles tab ──────────────────────────────────────────────
          // Single source of truth for role configuration. Lists every
          // active role with × delete (except CIMT Leader), the inline
          // "add custom role" form, and the chip strip of hidden base
          // roles for one-click restore.
          <div style={{ padding: '0 0 16px' }}>
            <div style={{ padding: '14px 18px 8px', borderBottom: '1px solid var(--border)' }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--fg)', margin: 0 }}>CIMT Roles</h3>
              <p style={{ fontSize: 12, color: 'var(--muted)', margin: '4px 0 0' }}>
                Configure your organisation's CIMT structure — add custom roles, hide base roles you don't use, restore hidden ones.
                CIMT Leader is structural and cannot be removed (IAP sign-off depends on it).
              </p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.7fr) minmax(280px, 1fr)', alignItems: 'start' }}>
              <div style={{ minWidth: 0, borderRight: '1px solid var(--border)' }}>
            <table className="incidents-table" style={{ width: '100%', tableLayout: 'fixed', margin: 0, borderRadius: 0 }}>
              <thead><tr>
                <th style={{ width: '60%' }}>Role</th>
                <th style={{ width: '20%' }}>Type</th>
                <th style={{ width: '20%' }}>Actions</th>
              </tr></thead>
              <tbody>{cimtRolesList.map(role => (
                <tr key={role.name}>
                  <td style={{ fontWeight: 600, fontSize: 12 }}>{role.name}</td>
                  <td>
                    {role.isCustom ? (
                      <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 3, background: 'var(--primary-dim)', color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: 0.3 }}>Custom</span>
                    ) : role.name === 'CIMT Leader' ? (
                      <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 3, background: 'var(--success-soft)', color: 'var(--success)', textTransform: 'uppercase', letterSpacing: 0.3 }}>Protected</span>
                    ) : (
                      <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 3, background: 'var(--surface)', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.3 }}>Base</span>
                    )}
                  </td>
                  <td>
                    {role.name !== 'CIMT Leader' ? (
                      <button
                        onClick={() => handleRemoveRole(role.name, role.isCustom)}
                        title={role.isCustom
                          ? `Permanently delete "${role.name}"`
                          : `Hide "${role.name}" from your CIMT structure (restorable)`}
                        style={{ background: 'transparent', border: '1px solid var(--border)', borderRadius: 5, padding: '3px 10px', fontSize: 11, color: 'var(--muted)', cursor: 'pointer', fontFamily: 'inherit' }}
                        onMouseEnter={e => { e.currentTarget.style.color = 'var(--accent-red)'; e.currentTarget.style.borderColor = 'var(--accent-red)'; }}
                        onMouseLeave={e => { e.currentTarget.style.color = 'var(--muted)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
                      >
                        {role.isCustom ? 'Delete' : 'Hide'}
                      </button>
                    ) : (
                      <span style={{ fontSize: 11, color: 'var(--subtle)' }}>—</span>
                    )}
                  </td>
                </tr>
              ))}</tbody>
            </table>

            {/* Add custom role — same handler as before, just lives here now. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 18px', borderTop: '1px solid var(--border)' }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--subtle)', textTransform: 'uppercase' }}>Add custom role</label>
              <input
                value={newRoleName}
                onChange={e => { setNewRoleName(e.target.value); if (roleError) setRoleError(''); }}
                onKeyDown={e => { if (e.key === 'Enter') handleAddCustomRole(); }}
                placeholder="e.g. Onboarding Lead"
                style={{ flex: 1, maxWidth: 280, padding: '5px 10px', fontSize: 12, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 5, color: 'var(--fg)' }}
                disabled={addingRole}
              />
              <button
                onClick={handleAddCustomRole}
                disabled={addingRole || !newRoleName.trim()}
                style={{
                  background: 'var(--primary)', color: 'var(--on-primary)', border: 'none', borderRadius: 5,
                  padding: '5px 14px', fontSize: 12, fontWeight: 700, cursor: addingRole || !newRoleName.trim() ? 'not-allowed' : 'pointer',
                  opacity: addingRole || !newRoleName.trim() ? 0.6 : 1, fontFamily: 'inherit',
                }}
              >{addingRole ? 'Adding…' : 'Add role'}</button>
              {roleError && <span style={{ fontSize: 11, color: 'var(--accent-red)' }}>{roleError}</span>}
            </div>

            {disabledRoles.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6, padding: '10px 18px 14px', borderTop: '1px solid var(--border)' }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--subtle)', textTransform: 'uppercase', marginRight: 4 }}>
                  Hidden ({disabledRoles.length}) — click to restore
                </label>
                {disabledRoles.map(name => (
                  <button
                    key={name}
                    onClick={() => handleRestoreRole(name)}
                    title={`Restore "${name}" to your CIMT structure`}
                    style={{
                      background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14,
                      padding: '3px 10px 3px 8px', fontSize: 11, color: 'var(--muted)',
                      cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 4,
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'var(--primary-dim)'; e.currentTarget.style.color = 'var(--primary)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'var(--surface)'; e.currentTarget.style.color = 'var(--muted)'; }}
                  >
                    <span style={{ fontSize: 12, lineHeight: 1, fontWeight: 700 }}>+</span>
                    {name}
                  </button>
                ))}
              </div>
            )}
              </div>

              <aside style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 20 }}>
                <div>
                  <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--subtle)', marginBottom: 11 }}>At a glance</div>
                  <div style={{ display: 'flex', gap: 10 }}>
                    {[
                      { n: cimtRolesList.length, l: 'Active' },
                      { n: cimtRolesList.filter(r => r.isCustom).length, l: 'Custom' },
                      { n: disabledRoles.length, l: 'Hidden' },
                    ].map(s => (
                      <div key={s.l} style={{ flex: 1, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '11px 13px' }}>
                        <div style={{ fontFamily: 'var(--font-heading)', fontSize: 21, fontWeight: 800, color: 'var(--fg)', lineHeight: 1 }}>{s.n}</div>
                        <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 5 }}>{s.l}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--subtle)', marginBottom: 11 }}>Role types</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
                    {[
                      { t: 'Protected', tone: 'var(--success)', d: 'Structural — CIMT Leader, required for IAP sign-off.' },
                      { t: 'Base', tone: 'var(--muted)', d: 'Standard roles. Hide any you do not use.' },
                      { t: 'Custom', tone: 'var(--primary)', d: 'Roles your organisation has added.' },
                    ].map(r => (
                      <div key={r.t} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                        <span style={{ marginTop: 3, width: 8, height: 8, borderRadius: 2, background: r.tone, flexShrink: 0 }} />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--fg)' }}>{r.t}</div>
                          <div style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.45 }}>{r.d}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <p style={{ fontSize: 11, color: 'var(--subtle)', lineHeight: 1.5, margin: 0 }}>
                  Hidden roles disappear from rosters and assignments but keep their history — restore them anytime.
                </p>
              </aside>
            </div>
          </div>
        ) : loading ? (
          <p style={{ color: 'var(--muted)', padding: 32, textAlign: 'center' }}>Loading...</p>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: 'var(--muted)' }}>
            <p style={{ fontSize: 15, marginBottom: 4 }}>{activeTab === 'staff' ? 'No active staff' : 'No pending invitations'}</p>
            <p style={{ fontSize: 12 }}>Click "Onboard Staff" to invite team members</p>
          </div>
        ) : (
          <table className="incidents-table" style={{ tableLayout: 'fixed', margin: 0, borderRadius: 0 }}>
            <thead><tr>
              <th style={{ width: '6%' }}>Actions</th>
              <th style={{ width: '16%' }}>Name</th>
              <th style={{ width: '17%' }}>Email</th>
              <th style={{ width: '7%' }}>Role</th>
              <th style={{ width: '12%' }}>CIMT Role</th>
              <th style={{ width: '10%' }}>Position</th>
              <th style={{ width: '10%' }}>Phone</th>
              <th style={{ width: '12%' }}>Availability</th>
              <th style={{ width: '6%' }}>Active</th>
              <th style={{ width: '7%' }}>Status</th>
            </tr></thead>
            <tbody>{filtered.map(s => (
              <tr key={s.id} style={{
                opacity: s.isActive ? 1 : 0.45,
                borderLeft: s.isOnLeave ? '3px solid var(--warning)' : undefined,
                background: s.isOnLeave ? 'color-mix(in srgb, var(--warning) 6%, transparent)' : undefined,
              }}>
                <td>
                  <ActionsDropdown>
                    <ActionItem label="Edit" onClick={() => setEditTarget(s)} />
                    {activeTab === 'invitations' && (
                      <ActionItem label={copiedId === s.id ? 'Link Copied!' : s.inviteSentAt ? 'Resend Invite' : 'Send Invite'} onClick={() => handleResendInvite(s)} />
                    )}
                    {activeTab === 'staff' && (
                      <ActionItem label={s.isOnLeave ? 'Return from Leave' : 'Mark On Leave'} onClick={() => handleToggleLeave(s)} />
                    )}
                    {activeTab === 'staff' && (
                      <ActionItem label={s.isActive ? 'Deactivate' : 'Activate'} onClick={() => handleToggleActive(s)} />
                    )}
                    <ActionItem label="Delete" color="var(--accent-red)" onClick={() => handleDelete(s)} />
                  </ActionsDropdown>
                </td>
                <td>
                  <div style={{ fontWeight: 600, fontSize: 12 }}>{s.firstName} {s.lastName}</div>
                </td>
                <td style={{ fontSize: 11, color: 'var(--muted)' }}>{s.email}</td>
                <td>
                  <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
                    background: `${ROLE_COLOURS[s.systemRole]}20`, color: ROLE_COLOURS[s.systemRole], textTransform: 'uppercase' }}>
                    {s.systemRole}
                  </span>
                </td>
                <td style={{ fontSize: 11, color: s.cimtRole ? 'var(--fg)' : 'var(--subtle)' }}>{s.cimtRole ?? '—'}</td>
                <td style={{ fontSize: 11, color: 'var(--muted)' }}>{s.jobTitle ?? '—'}</td>
                <td style={{ fontSize: 11, color: 'var(--muted)' }}>{s.phone ?? '—'}</td>
                <td>
                  <div style={{ display: 'flex', gap: 3 }}>
                    {DAYS.map(d => {
                      const on = s.availableDays.includes(d);
                      return (
                        <span key={d} title={`${d} — ${on ? 'available' : 'unavailable'}`} style={{
                          width: 19, height: 19, borderRadius: '50%', display: 'inline-flex', alignItems: 'center',
                          justifyContent: 'center', fontSize: 9, fontWeight: 700, letterSpacing: '-0.02em',
                          background: on ? 'var(--primary-mid)' : 'var(--faint)',
                          color: on ? 'var(--primary)' : 'var(--subtle)',
                          border: `1px solid ${on ? 'var(--primary-border)' : 'transparent'}`,
                        }}>{DAY_SHORT[d]}</span>
                      );
                    })}
                  </div>
                </td>
                <td>
                  <span style={{
                    width: 8, height: 8, borderRadius: '50%', display: 'inline-block',
                    background: s.isActive ? 'var(--success)' : '#9ca3af', marginRight: 4, verticalAlign: 'middle',
                  }} />
                  <span style={{ fontSize: 10, color: s.isActive ? 'var(--success)' : '#9ca3af' }}>
                    {s.isActive ? 'Yes' : 'No'}
                  </span>
                </td>
                <td>
                  {(() => {
                    const st = activeTab === 'invitations'
                      ? (s.inviteSentAt ? { label: 'Pending', tone: 'var(--warning)' } : { label: 'Not sent', tone: 'var(--subtle)' })
                      : s.isOnLeave ? { label: 'On leave', tone: 'var(--warning)' }
                      : { label: 'Active', tone: 'var(--success)' };
                    return (
                      <span style={{
                        fontSize: 9.5, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
                        padding: '3px 9px', borderRadius: 999, whiteSpace: 'nowrap',
                        background: `color-mix(in srgb, ${st.tone} 14%, transparent)`, color: st.tone,
                      }}>{st.label}</span>
                    );
                  })()}
                </td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </div>

      {showInvite && ReactDOM.createPortal(
        <InviteModal onDone={() => { load(); refreshCimtRoles(); }} onClose={() => setShowInvite(false)} availableRoles={availableRoles} />,
        document.body,
      )}
      {editTarget && ReactDOM.createPortal(
        <EditModal staff={editTarget} onSave={handleUpdate} onClose={() => setEditTarget(null)} availableRoles={availableRoles} />,
        document.body,
      )}
    </div>
  );
}

// ── Manual roster reminder trigger (admin/manager only) ─────────────────────
// Fires the same Lambda the Friday cron fires. While TEST_RECIPIENT is set,
// every email is redirected to yash9871123@gmail.com — see the prod cleanup
// note in lambda/roster-reminder/index.ts before going live.
// ── Roster avatar + custom assignment dropdown ──────────────────────────
const ROSTER_AVATAR_COLORS = ['#0067FE', '#0088CB', '#0E8C74', '#B45309', '#BE123C', '#1D6FB8', '#0e7490', '#0d9488'];
function rosterInitials(name: string): string {
  const p = name.trim().split(/\s+/);
  return ((p[0]?.[0] || '') + (p[1]?.[0] || '')).toUpperCase() || '?';
}
function rosterAvatarColor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return ROSTER_AVATAR_COLORS[h % ROSTER_AVATAR_COLORS.length];
}
function RosterAvatar({ name, size = 28 }: { name: string; size?: number }) {
  return (
    <span style={{ width: size, height: size, borderRadius: '50%', flexShrink: 0, display: 'grid', placeItems: 'center', background: rosterAvatarColor(name), color: '#fff', fontSize: Math.round(size * 0.38), fontWeight: 700, letterSpacing: '0.02em' }}>
      {rosterInitials(name)}
    </span>
  );
}

type RosterOpt = { id: number; name: string; jobTitle?: string };
// Custom person-picker: avatar + name + job title, grouped, click-outside close.
function RosterSlot({ placeholder, selected, groups, onSelect }: {
  placeholder: string;
  selected: RosterOpt | null;
  groups: { label: string; items: RosterOpt[] }[];
  onSelect: (id: number | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    window.addEventListener('mousedown', h);
    return () => window.removeEventListener('mousedown', h);
  }, [open]);
  const anyItems = groups.some(g => g.items.length > 0);
  const twoLine = (name: string, jobTitle?: string) => (
    <span style={{ minWidth: 0, flex: 1 }}>
      <span style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: 'var(--fg)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
      {jobTitle && <span style={{ display: 'block', fontSize: 11, color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{jobTitle}</span>}
    </span>
  );
  return (
    <div ref={ref} style={{ position: 'relative', minWidth: 0 }}>
      <button
        type="button" onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', minWidth: 0, display: 'flex', alignItems: 'center', gap: 9,
          padding: '7px 10px', borderRadius: 10, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
          background: 'var(--surface)', border: `1px solid ${open ? 'var(--primary)' : 'var(--border-md)'}`,
          boxShadow: open ? '0 0 0 3px var(--primary-dim)' : 'none', transition: 'border-color .12s, box-shadow .12s',
        }}
      >
        {selected ? (
          <><RosterAvatar name={selected.name} size={28} />{twoLine(selected.name, selected.jobTitle)}</>
        ) : (
          <>
            <span style={{ width: 28, height: 28, borderRadius: '50%', flexShrink: 0, display: 'grid', placeItems: 'center', border: '1.5px dashed var(--border-md)', color: 'var(--subtle)' }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14" /></svg>
            </span>
            <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: 'var(--subtle)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>— {placeholder} —</span>
          </>
        )}
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--subtle)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .12s' }}><path d="m6 9 6 6 6-6" /></svg>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, minWidth: 240, zIndex: 40,
          background: 'var(--card)', border: '1px solid var(--border-md)', borderRadius: 12,
          boxShadow: '0 16px 40px rgba(20,20,45,0.18)', maxHeight: 288, overflowY: 'auto', padding: 5,
        }}>
          {selected && (
            <button type="button" onClick={() => { onSelect(null); setOpen(false); }} className="roster-opt"
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '7px 9px', borderRadius: 8, cursor: 'pointer', background: 'transparent', border: 'none', textAlign: 'left', fontFamily: 'inherit', color: 'var(--muted)', fontSize: 12.5, fontWeight: 600 }}>
              <span style={{ width: 28, height: 28, borderRadius: '50%', flexShrink: 0, display: 'grid', placeItems: 'center', border: '1.5px dashed var(--border-md)', color: 'var(--subtle)' }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
              </span>
              Clear selection
            </button>
          )}
          {anyItems ? groups.map(g => (
            <div key={g.label}>
              <div style={{ padding: '7px 10px 4px', fontSize: 9.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--subtle)' }}>{g.label}</div>
              {g.items.map(o => (
                <button key={o.id} type="button" onClick={() => { onSelect(o.id); setOpen(false); }} className="roster-opt"
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '7px 9px', borderRadius: 8, cursor: 'pointer', background: 'transparent', border: 'none', textAlign: 'left', fontFamily: 'inherit' }}>
                  <RosterAvatar name={o.name} size={28} />{twoLine(o.name, o.jobTitle)}
                </button>
              ))}
            </div>
          )) : <div style={{ padding: '14px 12px', fontSize: 12, color: 'var(--subtle)', textAlign: 'center' }}>No staff available</div>}
        </div>
      )}
    </div>
  );
}

function SendReminderButton() {
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [resultColor, setResultColor] = useState<'success' | 'error'>('success');

  async function fire() {
    if (sending) return;
    setSending(true);
    setResult(null);
    try {
      const res = await sendRosterReminder();
      const r = res.result;
      const summary = `${r.emailsSent ?? 0} email(s) sent${r.testMode ? ` (test mode → ${r.testRecipient})` : ''}`;
      setResultColor('success');
      setResult(summary);
      // Auto-clear after 6s so it doesn't linger
      setTimeout(() => setResult(null), 6000);
    } catch (err: any) {
      setResultColor('error');
      setResult(err.message || 'Failed to send reminder');
      setTimeout(() => setResult(null), 8000);
    } finally {
      setSending(false);
    }
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      {result && (
        <span style={{
          fontSize: 11, fontWeight: 600,
          color: resultColor === 'success' ? 'var(--success)' : 'var(--accent-red)',
        }}>
          {resultColor === 'success' ? '✓ ' : '⚠ '}{result}
        </span>
      )}
      <button
        onClick={fire}
        disabled={sending}
        title="Manually send the weekly roster reminder email to admins/managers now (instead of waiting for the Friday cron)."
        style={{
          background: sending ? 'var(--faint)' : 'var(--primary)',
          color: sending ? 'var(--muted)' : 'var(--on-primary)',
          border: 'none', borderRadius: 9,
          padding: '9px 16px', fontSize: 12.5, fontWeight: 700,
          cursor: sending ? 'wait' : 'pointer',
          fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 8, flexShrink: 0,
          boxShadow: sending ? 'none' : '0 4px 12px -4px color-mix(in srgb, var(--primary) 55%, transparent)',
        }}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>
        {sending ? 'Sending…' : 'Send reminder now'}
      </button>
    </div>
  );
}

// ── Appearance / theme settings ──────────────────────────────────────────────
function AppearanceSettings() {
  const { theme, setTheme } = useTheme();

  // Live preview swatches for each theme so users can see what they'll get
  // before committing. Hex values mirror the CSS in index.css.
  const themes: { key: Theme; name: string; bg: string; surface: string; primary: string; fg: string; description: string }[] = [
    {
      key: 'dark',
      name: 'Dark (default)',
      bg: '#0A1020', surface: '#16213A', primary: '#3B82F6', fg: '#EAF1FF',
      description: 'Deep navy with electric-blue accents — best for incident rooms and low-light environments.',
    },
    {
      key: 'light',
      name: 'Light',
      bg: '#FAFAFA', surface: '#ffffff', primary: '#0067FE', fg: '#1F1F1F',
      description: 'White surfaces with electric-blue accents.',
    },
  ];

  return (
    <div style={{ padding: 28 }}>
      <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--fg)', marginBottom: 6 }}>
        Appearance
      </h3>
      <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 24 }}>
        Choose how CIMA looks for you. Your selection is saved on this device.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, maxWidth: 720 }}>
        {themes.map(t => {
          const selected = theme === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTheme(t.key)}
              style={{
                textAlign: 'left',
                background: 'var(--surface)',
                border: selected ? '2px solid var(--primary)' : '2px solid var(--border-md)',
                borderRadius: 12,
                padding: 0, overflow: 'hidden',
                cursor: 'pointer',
                fontFamily: 'inherit',
                boxShadow: selected ? '0 0 0 4px var(--primary-dim)' : 'none',
                transition: 'border-color 0.15s, box-shadow 0.15s',
              }}
            >
              {/* Live theme preview block */}
              <div style={{
                background: t.bg, padding: 16, borderBottom: '1px solid var(--border)',
                position: 'relative',
              }}>
                <div style={{
                  display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12,
                }}>
                  <div style={{ width: 18, height: 18, borderRadius: 4, background: t.primary }} />
                  <div style={{ height: 8, flex: 1, borderRadius: 4, background: t.surface, opacity: 0.7 }} />
                </div>
                <div style={{
                  background: t.surface, color: t.fg,
                  padding: '10px 12px', borderRadius: 8,
                  fontFamily: 'var(--font-heading)', fontSize: 14, fontWeight: 600,
                }}>
                  Sample heading
                </div>
                <div style={{
                  marginTop: 8, padding: '6px 10px', borderRadius: 6,
                  background: t.primary, color: '#fff',
                  fontSize: 11, fontWeight: 600, display: 'inline-block',
                }}>Primary button</div>
                {selected && (
                  <div style={{
                    position: 'absolute', top: 10, right: 10,
                    width: 22, height: 22, borderRadius: '50%',
                    background: 'var(--primary)', color: 'var(--on-primary)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 12, fontWeight: 700,
                  }}>✓</div>
                )}
              </div>

              {/* Label + description */}
              <div style={{ padding: '14px 16px' }}>
                <div style={{
                  fontSize: 14, fontWeight: 700, color: 'var(--fg)',
                  marginBottom: 4, fontFamily: 'var(--font-heading)',
                }}>{t.name}</div>
                <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.45 }}>
                  {t.description}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
